import type {
    HolisticSummary,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React, { useCallback } from 'react';
import {
    ArrowRightIcon,
    ArrowUpRightIcon,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CalculatorIcon,
    Icon,
    NavigationTile,
    NavigationTileWithSecondary,
    XStack,
    YStack,
} from 'smoothie';

import { useMoneyMapEarningsInputs } from '../hooks/useMoneyMapEarningsInputs';
import { useOpenCalculatorWithTimer } from '../hooks/useOpenCalculatorWithTimer';
import { type EventTrackingFunction } from '../types/eventTracking';
import { formatEarnings } from '../utils/earningsFormulas';
import { MoneyMapEarningsCalculatorFrame } from './Calculator';

/** Mint panel backgrounds for Money Mapper left rail (expanded card + collapsed trigger). */
const MONEY_MAP_EARNINGS_MINT = '#D4EDE4';

const hasEnoughEarningsToShow = (annualEarningsDefault: number) => {
    // Business rule from June 2025: Earnings must be higher than new core monthly price x 12 to show calculator
    return annualEarningsDefault > 48 * 12;
};

export const MoneyMapEarningsTile = ({
    moneyMap,
    isMoneyMapCompleted,
    holisticSummary,
    calculatorPrimaryButtonText,
    onPressCalculatorPrimaryButton,
    onInteractionEvent,
    appContext = '',
}: {
    moneyMap: MoneyMapWithRulesAndAccounts;
    isMoneyMapCompleted: boolean;
    holisticSummary: HolisticSummary;
    calculatorPrimaryButtonText?: string;
    onPressCalculatorPrimaryButton?: () => void;
    onInteractionEvent?: EventTrackingFunction;
    appContext?: string;
}) => {
    const { annualEarningsDefault } = useMoneyMapEarningsInputs({
        moneyMap,
        holisticSummary,
    });

    const interactionEventData = {
        appContext,
        isMoneyMapCompleted,
        moneyMapStatus: moneyMap.status,
    };

    const {
        isCalculatorOpen,
        handleOpenCalculator,
        handleCloseCalculator,
        handleCalculatorPrimaryButtonPress,
    } = useOpenCalculatorWithTimer({
        onInteractionEvent,
        interactionEventData,
        onPressCalculatorPrimaryButton,
    });

    if (!hasEnoughEarningsToShow(annualEarningsDefault)) {
        return null;
    }

    return (
        <>
            <NavigationTile
                title={`${formatEarnings(annualEarningsDefault)} estimated earnings`}
                description="based on your Money Map"
                tone="positive"
                iconLeft={CalculatorIcon}
                iconRight={ArrowUpRightIcon}
                isPrivate={true}
                onPress={handleOpenCalculator}
            />
            <MoneyMapEarningsCalculatorFrame
                moneyMap={moneyMap}
                holisticSummary={holisticSummary}
                isOpen={isCalculatorOpen}
                onClose={handleCloseCalculator}
                primaryButtonText={calculatorPrimaryButtonText}
                onPressPrimaryButton={handleCalculatorPrimaryButtonPress}
            />
        </>
    );
};

export const DirectDepositWithEarningsTile = ({
    onPressDirectDeposit,
    moneyMap,
    holisticSummary,
    onInteractionEvent,
    appContext = '',
}: {
    onPressDirectDeposit: (source: string) => void;
    moneyMap: MoneyMapWithRulesAndAccounts;
    holisticSummary: HolisticSummary;
    onInteractionEvent?: EventTrackingFunction;
    appContext?: string;
}) => {
    const { annualEarningsDefault } = useMoneyMapEarningsInputs({
        moneyMap,
        holisticSummary,
    });

    const interactionEventData = {
        appContext,
    };

    const {
        isCalculatorOpen,
        handleOpenCalculator,
        handleCloseCalculator,
        handleCalculatorPrimaryButtonPress,
    } = useOpenCalculatorWithTimer({
        onInteractionEvent,
        interactionEventData,
        onPressCalculatorPrimaryButton: () => {
            onPressDirectDeposit('earnings-calculator');
        },
    });

    const handleNavigationTilePrimaryActionPress = useCallback(() => {
        onPressDirectDeposit('roadmap-dd-prompt-with-earnings');
    }, [onPressDirectDeposit]);

    if (!hasEnoughEarningsToShow(annualEarningsDefault)) {
        return null;
    }

    return (
        <>
            <NavigationTileWithSecondary
                tone="positive"
                title="Set up direct deposit"
                description={`Earn up to ${formatEarnings(annualEarningsDefault)} this year based on your Money Map`}
                iconRight={ArrowRightIcon}
                isPrivate={true}
                onPress={handleNavigationTilePrimaryActionPress}
                secondaryTitle="Calculate my earnings"
                secondaryIcon={CalculatorIcon}
                secondaryOnPress={handleOpenCalculator}
            />
            <MoneyMapEarningsCalculatorFrame
                moneyMap={moneyMap}
                holisticSummary={holisticSummary}
                isOpen={isCalculatorOpen}
                onClose={handleCloseCalculator}
                primaryButtonText="Set up direct deposit"
                onPressPrimaryButton={handleCalculatorPrimaryButtonPress}
            />
        </>
    );
};

export type GroveMoneyMapCalculatorLayout =
    | 'default'
    | 'sidebarExpanded'
    | 'sidebarCollapsed';

export const GroveMoneyMapCalculatorButton = ({
    moneyMap,
    holisticSummary,
    onInteractionEvent,
    appContext = '',
    compact = false,
    layout,
}: {
    moneyMap: MoneyMapWithRulesAndAccounts;
    holisticSummary: HolisticSummary;
    onInteractionEvent?: EventTrackingFunction;
    appContext?: string;
    /** @deprecated Prefer `layout`. Icon-only trigger when layout is default. */
    compact?: boolean;
    layout?: GroveMoneyMapCalculatorLayout;
}) => {
    const { annualEarningsDefault } = useMoneyMapEarningsInputs({
        moneyMap,
        holisticSummary,
    });

    const interactionEventData = {
        appContext,
    };

    const { isCalculatorOpen, handleOpenCalculator, handleCloseCalculator } =
        useOpenCalculatorWithTimer({
            onInteractionEvent,
            interactionEventData,
        });

    const resolvedLayout: GroveMoneyMapCalculatorLayout =
        layout ?? (compact ? 'sidebarCollapsed' : 'default');

    const trigger =
        resolvedLayout === 'sidebarCollapsed' ? (
            <XStack
                width={44}
                height={44}
                borderRadius={12}
                backgroundColor={MONEY_MAP_EARNINGS_MINT}
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                hoverStyle={{ opacity: 0.92 }}
                pressStyle={{ opacity: 0.85, scale: 0.98 }}
                onPress={handleOpenCalculator}
            >
                <Icon icon={CalculatorIcon} size={20} color="$gray1100" />
            </XStack>
        ) : resolvedLayout === 'sidebarExpanded' ? (
            <YStack
                backgroundColor={MONEY_MAP_EARNINGS_MINT}
                borderRadius={16}
                padding={14}
                gap={10}
                width="100%"
            >
                <BodySmallRegular color="$gray1100">
                    Estimated earnings based on your Money Map
                </BodySmallRegular>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.TINY}
                    width="auto"
                    tone="neutral"
                    icon={CalculatorIcon}
                    text={formatEarnings(annualEarningsDefault)}
                    onPress={handleOpenCalculator}
                    buttonProps={{
                        alignSelf: 'flex-start',
                    }}
                />
            </YStack>
        ) : (
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.TINY}
                width="auto"
                tone="positive"
                icon={CalculatorIcon}
                text={formatEarnings(annualEarningsDefault)}
                onPress={handleOpenCalculator}
                buttonProps={{
                    paddingHorizontal: 20,
                }}
            />
        );

    return (
        <>
            {trigger}
            <MoneyMapEarningsCalculatorFrame
                isInGrove={true}
                moneyMap={moneyMap}
                holisticSummary={holisticSummary}
                isOpen={isCalculatorOpen}
                onClose={handleCloseCalculator}
            />
        </>
    );
};
